import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
	useAddMemberToProjectMutation,
	useRemoveMemberFromProjectMutation
} from '@features/project-management-tools/api/projectManagementApi';
import { setProjectMembers } from '@features/project-management-tools/redux/myProjectDetailsSlice';

import useDebounce from '@/hooks/useDebounce';
import useClickOutside from '@/hooks/useClickOutside';
import CommonImage from '@/components/CommonImage';
import TeamMember from '@/components/TeamMember';
import { toastError, toastSuccess } from '@/components/Toast';
import ApiClient from '@/lib/apiClient';
import { closeLoader, openLoader } from '@/store/actions/LoaderAction';
import { printApiError } from '@/utils/Util';
import { useMyProjectDetails } from '@/store/reducers';
import { useGetUserByTypeQuery } from '@/api/commonApis';

const DEFAULT_USER_PIC = '/images/pro_pic_default.svg';
const MAX_MEMBERS = 4;

const AddMemberToProject = React.memo(({ projectId }) => {
	const { projectMembers } = useMyProjectDetails();
	const [addMemberToProject] = useAddMemberToProjectMutation();
	const [removeMemberFromProject] = useRemoveMemberFromProjectMutation();
	const { data: allUser } = useGetUserByTypeQuery({
		params: {
			userTypes:
				'PROJECT_MANAGER,FASHION_DESIGNER,MERCHANDISER,SAMPLE_DEVELOPMENT,PRODUCT_DEVELOPMENT,COSTING_MANAGER,QA'
		}
	});

	const [showAddMemberModal, setShowAddMemberModal] = useState(false);
	const [searchUserSuggestions, setSearchUserSuggestions] = useState([]);
	const [searchUserText, setSearchUserText] = useState('');
	const dispatch = useDispatch();
	const AddNewMemberButton = useRef(null);
	const AddNewMemberModal = useRef(null);

	const debouncedSearchUserText = useDebounce(searchUserText, 300);

	useClickOutside(AddNewMemberModal, () => {
		if (!AddNewMemberButton.current?.contains(document.activeElement)) {
			setShowAddMemberModal(false);
		}
	});

	const MEMBER_DATA = useMemo(
		() => ({
			usersByTypeList: allUser,
			searchUserText,
			searchUserSuggestions,
			memberList: projectMembers
		}),
		[allUser, searchUserText, searchUserSuggestions, projectMembers]
	);

	const getSearchSuggestions = useCallback(
		async (userInput) => {
			try {
				dispatch(openLoader());
				let params = `?email=${userInput}`;
				const { data } = await ApiClient.GET('getUserSuggestions', params);
				if (data) {
					setSearchUserSuggestions(data);
				}
			} catch (error) {
				printApiError(error);
			} finally {
				dispatch(closeLoader());
			}
		},
		[dispatch]
	);

	const onChangeTeam = useCallback((e) => {
		setSearchUserText(e.target.value);
	}, []);

	useEffect(() => {
		if (debouncedSearchUserText.trim()) {
			getSearchSuggestions(debouncedSearchUserText);
		} else {
			setSearchUserSuggestions([]);
		}
	}, [debouncedSearchUserText]);

	const toggleAddMemberModal = useCallback(() => {
		setShowAddMemberModal((prev) => !prev);
	}, []);

	const addUserToProject = useCallback(
		async (user) => {
			try {
				const isDuplicate = projectMembers.some(
					(member) => parseInt(member.id) === parseInt(user.id)
				);
				if (isDuplicate) {
					return;
				}
				dispatch(openLoader());
				await addMemberToProject({
					projectId,
					userId: user.id
				});

				dispatch(setProjectMembers([...projectMembers, { ...user, isRemovable: true }]));
				toastSuccess(`${user.name} added to this project`);
			} catch (error) {
				printApiError(error);
			} finally {
				dispatch(closeLoader());
			}
		},
		[projectMembers, dispatch, addMemberToProject, projectId]
	);

	const removeUserFromProject = useCallback(
		async (user) => {
			dispatch(openLoader());
			await removeMemberFromProject({
				projectId,
				userId: user.id
			})
				.unwrap()
				.then(() => {
					dispatch(setProjectMembers(projectMembers.filter((member) => member.id !== user.id)));
					toastSuccess(`${user.name} removed from this project`);
				})
				.catch((error) => {
					toastError(error?.data?.message || 'Something went wrong!');
				})
				.finally(() => {
					dispatch(closeLoader());
				});
		},
		[dispatch, removeMemberFromProject, projectId, projectMembers]
	);

	const renderIcon = useCallback(
		(user) => {
			return (
				<CommonImage
					key={user.id}
					onClick={toggleAddMemberModal}
					src={user?.profilePicDocUrl || user?.profilePicDocument?.docUrl}
					alt={user?.name}
					fallbackSrc={DEFAULT_USER_PIC}
					isStatic={user?.profilePicDocUrl || user?.profilePicDocument?.docUrl ? false : true}
				/>
			);
		},
		[toggleAddMemberModal]
	);

	const renderMemberIcon = useCallback(
		(userResponseList) => {
			if (userResponseList.length > MAX_MEMBERS) {
				return userResponseList.slice(0, MAX_MEMBERS).map((user) => renderIcon(user));
			} else {
				return userResponseList.map((user) => renderIcon(user));
			}
		},
		[renderIcon]
	);

	const handleMorePeopleClick = useCallback(
		(e) => {
			e.preventDefault();
			toggleAddMemberModal();
		},
		[toggleAddMemberModal]
	);

	return (
		<div className="position-relative">
			<div className="px-0 add-buyer d-flex justify-content-end">
				<div className="position-relative">
					<div className="added-members">
						<div
							id="AddNewMember"
							ref={AddNewMemberButton}
							className={`add-new-member ${showAddMemberModal ? 'show' : ''}`}
						>
							<CommonImage
								src="/icons/add-people.svg"
								alt="add"
								onClick={toggleAddMemberModal}
							/>
						</div>

						{projectMembers?.length > 0 && (
							<>
								{renderMemberIcon(projectMembers)}
								{projectMembers.length > MAX_MEMBERS && (
									<div className="more-people">
										<span onClick={handleMorePeopleClick}>
											+{projectMembers.length - MAX_MEMBERS}
										</span>
									</div>
								)}
							</>
						)}
					</div>
					<div
						className={`add-people-popup no-padding-popup shadow ${
							showAddMemberModal ? 'show' : ''
						}`}
						ref={AddNewMemberModal}
					>
						<TeamMember
							memberData={MEMBER_DATA}
							onAddMember={addUserToProject}
							onRemoveMember={removeUserFromProject}
							onChange={onChangeTeam}
							parentComponent="PROJECT_MANAGEMENT_TOOLS"
						/>
					</div>
				</div>
			</div>
		</div>
	);
});

export default AddMemberToProject;
